import React from 'react';
import { Loader } from 'src/components/core';
import _ from 'lodash';
import { ROUTES, CATEGORY_TYPES } from 'shared/constants';
import update from 'immutability-helper';
import { useNavigate } from 'react-router-dom';
import { LabSection } from '../lab-section';
import { scrollTo } from 'src/utils';
import { ActionButton, NotFound } from 'src/components/common';
import { LabCategoryEditor } from '../../lab-category/lab-category-editor';
import { LabCategoryDelete } from '../../lab-category/lab-category-delete';
import { AddSectionButton } from 'src/components/common/add-section';
import classNames from 'classnames';
import styles from '../../../Labs.module.scss';

const LabSectionListComponent = React.memo(function LabSectionListComponent({
  adminMode,
  list = null,
  isReady = true,
  fullPath = null,
  deleteCategory,
  updateCategorySubcategories = () => {},
  labSelectValues = () => {},
  category,
  id = null,
}) {
  const [categoryEntity, setCategory] = React.useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = React.useState(null);
  const [sections, setSections] = React.useState(list);

  const { ownerIds, tags } = category;

  React.useEffect(() => {
    setSections(list);
  }, [list]);

  const navigate = useNavigate();

  const changeSectionOrder = (id, atIndex) => {
    const { section, index } = findSection(id);
    setSections(
      update(sections, {
        $splice: [
          [index, 1],
          [atIndex, 0, section],
        ],
      }),
    );
    // Update the server with the new index
    finishDragSection(id, atIndex);
  };

  const findSection = id => {
    const section = sections.find(i => i.id === id);
    return {
      section,
      index: sections.indexOf(section),
    };
  };

  const finishDragSection = (categoryId, index) => {
    setTimeout(() => scrollTo(categoryId), 300);
    updateCategorySubcategories({
      id,
      categoryId,
      index,
    });
  };

  const createCategoryAction = React.useCallback(
    ({ path, isSectionCreation = false, ownerIds, tags }) => {
      // TODO: add analytics

      setCategory({ path, isSectionCreation, ownerIds, tags });
    },
    [],
  );

  const editCategoryAction = React.useCallback(
    ({ id, title, ownerIds, path, isSectionCreation = false, tags, drugClass, type }) => {
      // TODO: add analytics

      setCategory({ id, title, ownerIds, path, isSectionCreation, tags, drugClass, type });
    },
    [],
  );

  const deleteCategoryAction = React.useCallback(({ id, title, type, path, ownerIds }) => {
    setDeleteConfirmation({ id, title, type, path, ownerIds });
  }, []);

  const createSection = () => {
    // TODO: analytics
    createCategoryAction({ path: fullPath, isSectionCreation: true, ownerIds, tags });
  };

  const deleteFinish = React.useCallback(() => setDeleteConfirmation(null), []);

  const editorFinish = React.useCallback(
    ({ id, type, isSectionCreation = false, isNew = false }) => {
      setCategory(null);
      if (id && !isSectionCreation) {
        if (type === CATEGORY_TYPES.VALUES) {
          labSelectValues(id);
        } else if (isNew) {
          navigate(ROUTES.LABS_CATEGORIES.replace(':id', id));
        }
      }
    },
    [navigate, labSelectValues],
  );

  if (_.isNull(sections) || !isReady) {
    return <Loader />;
  }

  if (sections.length === 0 && !adminMode) {
    return <NotFound />;
  }

  const callToAction = sections.length === 0 && adminMode && !categoryEntity;

  return (
    <div className={classNames({ [styles.paddingBottom]: adminMode && !callToAction })}>
      {sections.map((section, index) => (
        <LabSection
          createCategory={createCategoryAction}
          deleteCategory={deleteCategoryAction}
          editCategory={editCategoryAction}
          key={`content-item-${section.id}`}
          firstItem={index === 0}
          lastItem={index === sections.length - 1}
          index={index}
          changeSectionOrder={changeSectionOrder}
          section={section}
          sectionsLength={sections.length}
        />
      ))}

      {callToAction && <AddSectionButton onClick={createSection} />}
      {adminMode && !callToAction && <ActionButton onClick={createSection} />}

      <LabCategoryEditor open={!!categoryEntity} onFinish={editorFinish} entity={categoryEntity} />

      <LabCategoryDelete
        isOpen={!!deleteConfirmation}
        entity={deleteConfirmation}
        deleteCategory={deleteCategory}
        onFinish={deleteFinish}
      />
    </div>
  );
});

export { LabSectionListComponent };
